import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { Plus, DoorOpen, Eye, Inbox as InboxIcon, ExternalLink, Loader2, ImagePlus } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const EMPTY_FORM = {
  title: "",
  property_address: "",
  city: "",
  state: "",
  zip_code: "",
  asking_price: "",
  arv: "",
  repair_estimate: "",
  beds: "",
  baths: "",
  sqft: "",
  year_built: "",
  description: "",
  exit_strategies: [],
  photo_urls_text: "",
  status: "live",
  close_by: "",
  emd_required: "",
};

const STRATEGY_OPTIONS = [
  { value: "wholesale", label: "Wholesale" },
  { value: "flip", label: "Fix & Flip" },
  { value: "brrrr", label: "BRRRR" },
  { value: "rental", label: "Buy & Hold" },
  { value: "novation", label: "Novation" },
  { value: "sub_to", label: "Sub-To" },
  { value: "seller_finance", label: "Seller Finance" },
];

const STATUS_BADGE = {
  draft: { text: "Draft", bg: "#F1F5F9", fg: "#475569" },
  live: { text: "Live", bg: "#DCFCE7", fg: "#166534" },
  under_contract: { text: "Under Contract", bg: "#FEF3C7", fg: "#78350F" },
  closed: { text: "Closed", bg: "#DBEAFE", fg: "#1E40AF" },
  archived: { text: "Archived", bg: "#F1F5F9", fg: "#94A3B8" },
};

export default function DispoRooms() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dispo/rooms");
      setRooms(data.rooms || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load Deal Rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleStrategy = (val) => {
    setForm((f) => ({
      ...f,
      exit_strategies: f.exit_strategies.includes(val)
        ? f.exit_strategies.filter((s) => s !== val)
        : [...f.exit_strategies, val],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        title: form.title,
        property_address: form.property_address,
        city: form.city || undefined,
        state: form.state || undefined,
        zip_code: form.zip_code || undefined,
        asking_price: form.asking_price ? Number(form.asking_price) : undefined,
        arv: form.arv ? Number(form.arv) : undefined,
        repair_estimate: form.repair_estimate ? Number(form.repair_estimate) : undefined,
        beds: form.beds ? Number(form.beds) : undefined,
        baths: form.baths ? Number(form.baths) : undefined,
        sqft: form.sqft ? Number(form.sqft) : undefined,
        year_built: form.year_built ? Number(form.year_built) : undefined,
        description: form.description || undefined,
        exit_strategies: form.exit_strategies,
        photo_urls: [],
        status: form.status,
        close_by: form.close_by || undefined,
        emd_required: form.emd_required ? Number(form.emd_required) : undefined,
      };
      await api.post("/dispo/rooms", payload);
      toast.success("Deal Room created 🎯");
      setForm(EMPTY_FORM);
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create room");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dispo-rooms-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <DoorOpen size={20} className="text-[var(--gold,#F59E0B)]" />
            <h1 className="font-display font-extrabold text-3xl tracking-tight">Deal Rooms</h1>
            <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Dispo Studio · Week 1</span>
          </div>
          <p className="text-sm text-[var(--ink-2)] max-w-2xl">
            Publish a polished, buyer-facing page for each disposition. Share the public link, collect offers, review them here.
          </p>
        </div>
        <button
          type="button"
          className="btn-money"
          onClick={() => setShowCreate((v) => !v)}
          data-testid="dispo-create-toggle-button"
        >
          <Plus size={16} />
          {showCreate ? "Close form" : "New Deal Room"}
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={submit}
          className="rounded-xl border border-[var(--line)] bg-white p-6 shadow-sm space-y-5"
          data-testid="dispo-create-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Deal title *" testid="dispo-form-title" value={form.title}
              onChange={(v) => setForm({ ...form, title: v })} required minLength={2} />
            <SelectField label="Status" testid="dispo-form-status" value={form.status}
              onChange={(v) => setForm({ ...form, status: v })}
              options={[{ v: "draft", l: "Draft (private)" }, { v: "live", l: "Live (accepting offers)" }]} />
          </div>
          <div>
            <label className="field-label">Property address *</label>
            <AddressAutocomplete
              name="property_address"
              placeholder="Start typing — we'll suggest verified addresses"
              inputClassName="input"
              dataTestid="dispo-form-address"
              value={form.property_address}
              onChange={(v) => setForm({ ...form, property_address: v })}
              onParsed={({ street, city, state, zip }) => setForm((prev) => ({
                ...prev,
                property_address: street || prev.property_address,
                city: city || prev.city,
                state: state || prev.state,
                zip_code: zip || prev.zip_code,
              }))}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextField label="City" testid="dispo-form-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <TextField label="State" testid="dispo-form-state" value={form.state} onChange={(v) => setForm({ ...form, state: v.toUpperCase().slice(0, 2) })} maxLength={2} />
            <TextField label="ZIP" testid="dispo-form-zip" value={form.zip_code} onChange={(v) => setForm({ ...form, zip_code: v })} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <TextField label="Asking $" type="number" testid="dispo-form-asking" value={form.asking_price} onChange={(v) => setForm({ ...form, asking_price: v })} />
            <TextField label="ARV $" type="number" testid="dispo-form-arv" value={form.arv} onChange={(v) => setForm({ ...form, arv: v })} />
            <TextField label="Repairs $" type="number" testid="dispo-form-repairs" value={form.repair_estimate} onChange={(v) => setForm({ ...form, repair_estimate: v })} />
            <TextField label="EMD required $" type="number" testid="dispo-form-emd" value={form.emd_required} onChange={(v) => setForm({ ...form, emd_required: v })} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <TextField label="Beds" type="number" testid="dispo-form-beds" value={form.beds} onChange={(v) => setForm({ ...form, beds: v })} />
            <TextField label="Baths" type="number" testid="dispo-form-baths" value={form.baths} onChange={(v) => setForm({ ...form, baths: v })} />
            <TextField label="Sq ft" type="number" testid="dispo-form-sqft" value={form.sqft} onChange={(v) => setForm({ ...form, sqft: v })} />
            <TextField label="Year built" type="number" testid="dispo-form-year" value={form.year_built} onChange={(v) => setForm({ ...form, year_built: v })} />
          </div>
          <TextField label="Offers due by (ISO date)" testid="dispo-form-close-by" value={form.close_by} onChange={(v) => setForm({ ...form, close_by: v })} placeholder="2026-08-15" />

          <div>
            <label className="field-label">Exit strategies</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {STRATEGY_OPTIONS.map((s) => {
                const active = form.exit_strategies.includes(s.value);
                return (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => toggleStrategy(s.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      active
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink-2)]"
                    }`}
                    data-testid={`dispo-form-strategy-${s.value}`}
                  >{s.label}</button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="field-label">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input min-h-[100px] resize-y"
              maxLength={5000}
              placeholder="What makes this deal work? Motivation, condition, exit thesis, comps commentary…"
              data-testid="dispo-form-description"
            />
          </div>

          <div>
            <label className="field-label">Photos</label>
            <div className="rounded-lg border border-dashed border-[var(--line)] bg-[#F8FAFC] p-3 text-xs text-[var(--ink-2)] flex items-center gap-2">
              <ImagePlus size={14} className="text-[var(--ink-3)]" />
              Save the room first, then drag &amp; drop photos on the detail page.
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="btn-money" disabled={creating} data-testid="dispo-form-submit">
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              {creating ? "Creating…" : "Create Deal Room"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setForm(EMPTY_FORM); setShowCreate(false); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="p-10 text-center text-[var(--ink-3)]" data-testid="dispo-rooms-loading">Loading…</div>
      ) : rooms.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-[var(--line)] p-10 text-center bg-white"
          data-testid="dispo-rooms-empty"
        >
          <DoorOpen size={40} className="mx-auto text-[var(--ink-3)] mb-3" />
          <h3 className="font-display font-bold text-xl mb-1">Your first Deal Room</h3>
          <p className="text-sm text-[var(--ink-2)] mb-4 max-w-md mx-auto">
            Turn any deal into a shareable, buyer-facing page. Collect offers directly — no more group texts of Zillow links.
          </p>
          <button type="button" className="btn-money" onClick={() => setShowCreate(true)} data-testid="dispo-empty-cta">
            <Plus size={16} />Create your first Deal Room
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="dispo-rooms-list">
          {rooms.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-[var(--line)] bg-white overflow-hidden shadow-sm hover:shadow-md transition"
                data-testid={`dispo-room-card-${r.id}`}
              >
                <div className="aspect-[16/9] bg-[var(--line)] overflow-hidden relative">
                  {(r.photo_urls || []).length > 0 ? (
                    <img src={r.photo_urls[0]} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[var(--ink-3)]">
                      <DoorOpen size={40} />
                    </div>
                  )}
                  <span
                    className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded font-bold uppercase tracking-wider"
                    style={{ background: badge.bg, color: badge.fg }}
                  >{badge.text}</span>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <h3 className="font-display font-bold text-lg leading-snug">{r.title}</h3>
                    <div className="text-xs text-[var(--ink-2)] mt-0.5">{r.property_address}</div>
                  </div>
                  <div className="flex gap-4 text-xs text-[var(--ink-2)]">
                    <span><strong className="text-[var(--ink)]">{fmtMoney(r.asking_price)}</strong> ask</span>
                    <span>ARV {fmtMoney(r.arv)}</span>
                    <span className="flex items-center gap-1"><Eye size={12} />{r.views ?? 0}</span>
                    <span className="flex items-center gap-1"><InboxIcon size={12} />{r.offer_count ?? 0} offers</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Link to={`/app/dispo/${r.id}`} className="btn-ghost text-sm" data-testid={`dispo-room-manage-${r.id}`}>
                      Manage
                    </Link>
                    <a
                      href={`/deal/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost text-sm flex items-center gap-1"
                      data-testid={`dispo-room-public-${r.id}`}
                    >
                      Public page <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        data-testid={testid}
        {...rest}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
